import { useEffect } from 'react';

import { useDepartments } from '../../lib/queries';
import { SearchSelect } from '../SearchSelect';

import type { CSSProperties } from 'react';

interface DepartmentSelectProps {
  companyId: string | undefined;
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  /**
   * When set, only cost centers whose owning branch (col B of the master)
   * matches are listed — so picking a branch first narrows the cost-center
   * options to the ones usable at that branch (a retail branch surfaces just
   * its own cost center; HQ / a factory surface their functional departments).
   */
  branchId?: string;
  searchable?: boolean;
}

/**
 * Reusable หน่วยงาน (department / cost-centre) picker bound to the active
 * company's department master. Empty value = none selected.
 */
export function DepartmentSelect({
  companyId,
  value,
  onChange,
  required,
  disabled,
  style,
  branchId,
  searchable,
}: DepartmentSelectProps) {
  const { data: departments = [] } = useDepartments(companyId, true);
  const options = branchId ? departments.filter((d) => d.branchId === branchId) : departments;

  // When a branch filter is active and the current selection is not (or no
  // longer) usable at that branch — e.g. the user changed the branch after
  // picking a cost center — clear it so a now-invalid value can't be submitted
  // silently. Guard on `departments.length` so we never clear while the list is
  // still loading.
  useEffect(() => {
    if (!branchId || !value || departments.length === 0) return;
    const stillValid = departments.some((d) => d.id === value && d.branchId === branchId);
    if (!stillValid) onChange('');
  }, [branchId, value, departments, onChange]);

  const placeholder = required ? '— เลือกหน่วยงาน * —' : '— หน่วยงาน —';

  if (searchable) {
    return (
      <SearchSelect
        value={value}
        onChange={onChange}
        disabled={disabled ?? false}
        placeholder={placeholder}
        options={options.map((d) => ({ value: d.id, label: `${d.code} — ${d.nameTh}` }))}
        getLabel={(v) => {
          const d = departments.find((x) => x.id === v);
          return d ? `${d.code} — ${d.nameTh}` : '';
        }}
      />
    );
  }

  return (
    <select
      className="select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={style}
    >
      <option value="">{placeholder}</option>
      {options.map((d) => (
        <option key={d.id} value={d.id}>
          {d.code} — {d.nameTh}
        </option>
      ))}
    </select>
  );
}
